import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ICommentModel } from '@fi-shared/models';
import { ObjectTypes } from '@fi-shared/types';
import { ICommentActionData } from '../../fi-comments';

export interface CommentState {
    comments: ICommentModel[];
    flattenAllComments: ICommentModel[];
    visibleComments: ICommentModel[];
}

export class CommentsState {
    private _deleteComment$ = new Subject<ICommentActionData>();
    private _saveComment$ = new Subject<ICommentActionData>();
    private _comments$ = new Map<string, BehaviorSubject<ICommentModel[]>>();
    private _flattenAllComments = new Map<string, ICommentModel[]>();
    private _visibleComments$ = new Map<string, BehaviorSubject<ICommentModel[]>>();
    private _commentsUpdated$ = new Subject<number>();

    getKey(objectType: ObjectTypes, objectId: number): string {
        return `${objectType}_${objectId}`;
    }

    getOrCreateState(objectType: ObjectTypes, objectId: number): CommentState {
        const key = this.getKey(objectType, objectId);

        if (!this._comments$.has(key)) {
            this._comments$.set(key, new BehaviorSubject<ICommentModel[]>([]));
            this._flattenAllComments.set(key, []);
            this._visibleComments$.set(key, new BehaviorSubject<ICommentModel[]>([]));
        }

        const state = {
            comments: this._comments$.get(key)!.getValue(),
            flattenAllComments: this._flattenAllComments.get(key)!,
            visibleComments: this._visibleComments$.get(key)!.getValue(),
        };

        if (state.visibleComments.length === 0 && state.comments.length > 0) {
            this._visibleComments$.get(key)!.next(state.comments);
            state.visibleComments = state.comments;
        }

        return state;
    }

    updateState(objectType: ObjectTypes, objectId: number, state: Partial<CommentState>): void {
        const key = this.getKey(objectType, objectId);

        if (!this._comments$.has(key)) {
            this._comments$.set(key, new BehaviorSubject<ICommentModel[]>([]));
        }
        if (!this._visibleComments$.has(key)) {
            this._visibleComments$.set(key, new BehaviorSubject<ICommentModel[]>([]));
        }
        if (!this._flattenAllComments.has(key)) {
            this._flattenAllComments.set(key, []);
        }

        if (state.comments !== undefined) {
            this._comments$.get(key)!.next(state.comments);
        }
        if (state.flattenAllComments !== undefined) {
            this._flattenAllComments.set(key, state.flattenAllComments);
        }
        if (state.visibleComments !== undefined) {
            this._visibleComments$.get(key)!.next(state.visibleComments);
        }
    }

    getComments$(objectType: ObjectTypes, objectId: number): Observable<ICommentModel[]> {
        const key = this.getKey(objectType, objectId);
        if (!this._comments$.has(key)) {
            this._comments$.set(key, new BehaviorSubject<ICommentModel[]>([]));
        }
        return this._comments$.get(key)!.asObservable();
    }

    getVisibleComments$(objectType: ObjectTypes, objectId: number): Observable<ICommentModel[]> {
        const key = this.getKey(objectType, objectId);
        if (!this._visibleComments$.has(key)) {
            this._visibleComments$.set(key, new BehaviorSubject<ICommentModel[]>([]));
        }
        return this._visibleComments$.get(key)!.asObservable();
    }

    getComments(objectType: ObjectTypes, objectId: number): ICommentModel[] {
        const key = this.getKey(objectType, objectId);
        if (!this._comments$.has(key)) {
            this._comments$.set(key, new BehaviorSubject<ICommentModel[]>([]));
        }
        return this._comments$.get(key)!.getValue();
    }

    getVisibleComments(objectType: ObjectTypes, objectId: number): ICommentModel[] {
        const key = this.getKey(objectType, objectId);
        if (!this._visibleComments$.has(key)) {
            this._visibleComments$.set(key, new BehaviorSubject<ICommentModel[]>([]));
        }
        const visibleComments = this._visibleComments$.get(key)!.getValue();

        if (visibleComments.length === 0) {
            const allComments = this.getComments(objectType, objectId);
            if (allComments.length > 0) {
                this._visibleComments$.get(key)!.next(allComments);
                return allComments;
            }
        }

        return visibleComments;
    }

    getFlattenComments(objectType: ObjectTypes, objectId: number): ICommentModel[] {
        const key = this.getKey(objectType, objectId);
        if (!this._flattenAllComments.has(key)) {
            this._flattenAllComments.set(key, []);
        }
        return this._flattenAllComments.get(key)!;
    }

    get deleteComment$(): Observable<ICommentActionData> {
        return this._deleteComment$.asObservable();
    }

    get saveComment$(): Observable<ICommentActionData> {
        return this._saveComment$.asObservable();
    }

    get commentsUpdated$(): Observable<number> {
        return this._commentsUpdated$.asObservable();
    }

    emitDeleteComment(commentActionData: ICommentActionData): void {
        this._deleteComment$.next(commentActionData);
    }

    emitSaveComment(commentActionData: ICommentActionData): void {
        this._saveComment$.next(commentActionData);
    }

    emitCommentsUpdated(commentId: number): void {
        this._commentsUpdated$.next(commentId);
    }
}
